import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {showNotification} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {defaultTo, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetTransferOrderQuery,
  useGetTransferOrderVariantQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  createHeaderActions,
  DetailTemplate,
  DetailTemplateHeader,
  EntityHeaderAction,
  EntityHeaderFlag,
  getUserName,
  handleApiError,
  NavigationItem,
  printPDF,
  Section,
  useChecklist,
  useFileNotification,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  isNotNilOrEmpty,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {IssueNotes} from './(sections)/IssueNotes/IssueNotes';
import {Overview} from './(sections)/Overview/Overview';

export function TransferOrderDetail() {
  const navigate = useNavigate();

  const {transferOrderVariantId, transferOrderId} = useWarehouseParams();

  const isDraft = isNotNilOrEmpty(transferOrderVariantId) && isNilOrEmpty(transferOrderId);

  const fileNotification = useFileNotification({onTimeout: () => showNotification.error()});

  const dateTimeFormatter = useDateTimeFormatter();

  const transferOrderQuery = useGetTransferOrderQuery(
    {transferOrderId},
    {skip: isNilOrEmpty(transferOrderId)}
  );

  const variantIdToFetch =
    transferOrderQuery.data?.transferOrderVariantId ?? transferOrderVariantId;

  const transferOrderVariantQuery = useGetTransferOrderVariantQuery(
    {transferOrderVariantId: variantIdToFetch},
    {skip: isNilOrEmpty(variantIdToFetch)}
  );

  const createdByUserQuery = useGetUserQuery(
    {id: transferOrderQuery.data?.createdBy as string},
    {skip: isNilOrEmpty(transferOrderQuery.data?.createdBy)}
  );

  const headerActionsQuery = useGetWarehouseHeaderDynamicActionsQuery(
    {id: transferOrderId, resource: 'transfer-order'},
    {skip: isNilOrEmpty(transferOrderId)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-transfer-order', contextId: transferOrderId},
    {skip: !transferOrderId}
  );

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-transfer-order',
    contextId: transferOrderId,
  });

  const [putHeaderAction, headerActionStatus] = usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: transferOrderId,
    resourceId: 'TRANSFER_ORDER',
  });

  const isLoading =
    transferOrderVariantQuery.isLoading ||
    transferOrderQuery.isLoading ||
    createdByUserQuery.isLoading ||
    headerActionsQuery.isFetching;

  const hasError =
    transferOrderVariantQuery.isError || transferOrderQuery.isError || headerActionsQuery.isError;

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const handleActionClick = (actionKey: string) =>
    match(actionKey)
      // Confirmation is handled centrally by createHeaderActions; returning the
      // promise keeps the delete dialog open with a loading state until it settles
      .with('delete', () =>
        putHeaderAction({
          id: transferOrderId,
          resource: 'transfer-order',
          body: {actionKey},
        })
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          // Non-empty options force same-tab navigation while the confirm
          // chord's Ctrl/⌘ may still be held (useNavigate would window.open)
          .then(() => navigate(warehouseRoutes.transfers, {replace: false}))
          .catch(handleApiError)
      )
      .with('prntTransferOrder', () => {
        postPrintoutOnDemand({
          body: {
            docOriginId: transferOrderId,
            originEntityPrintoutType: 'prntTransferOrder',
          },
        })
          .unwrap()
          .then((data) => {
            const fileId = data?.printout?.fileId;
            const pdfUrl = data?.printout?.pdfUrl;

            if (fileId) {
              fileNotification.registerFile(fileId, {
                onSuccess: () => {
                  if (pdfUrl) {
                    printPDF(pdfUrl);
                  } else {
                    showNotification.error();
                  }
                },
                onError: () => showNotification.error(),
              });
            } else {
              showNotification.error();
            }
          })
          .then(refreshDocumentsCount)
          .then(() =>
            showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
          )
          .then(() => {
            navigate(
              composePath(warehouseRoutes.transferOrderDetailDocuments, {
                params: {id: transferOrderId},
              })
            );
          })
          .catch(handleApiError);
      })
      .otherwise(() =>
        putHeaderAction({
          id: transferOrderId,
          resource: 'transfer-order',
          body: {actionKey},
        })
          .unwrap()
          .then(refreshDocumentsCount)
          .catch(handleApiError)
      );

  const headerActions = createHeaderActions({
    actions: headerActionsQuery.data?.actions,
    callback: handleActionClick,
    isLoading: headerActionStatus.isLoading || isPostPrintoutOnDemandLoading,
  });

  const numberParameter = transferOrderQuery.data?.number ?? EMPTY_PLACEHOLDER;

  const destinationTypeParameter = `${i18n.t('entity.warehouse.labels.destinationType')}: ${match(
    transferOrderQuery.data?.destinationType
  )
    .with('warehouse', () => i18n.t('entity.warehouse.labels.warehouse'))
    .with('authorization', () => i18n.t('entity.warehouse.labels.authorization'))
    .with('branch', () => i18n.t('entity.warehouse.labels.branch'))
    .with('external', () => i18n.t('entity.warehouse.labels.external'))
    .otherwise(() => EMPTY_PLACEHOLDER)}`;

  const transferOrderVariantName = transferOrderVariantQuery.data?.name;

  const variantParameter = `${i18n.t('entity.warehouse.labels.transferOrderVariant')}: ${transferOrderVariantName ?? EMPTY_PLACEHOLDER}`;

  const dateCreatedParameter = `${i18n.t('general.labels.dateCreated')}: ${formatToShortDate(transferOrderQuery.data?.createdAt)}`;

  const createdByParameter = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUserQuery?.data) ?? EMPTY_PLACEHOLDER}`;

  const headerFlags = match(transferOrderQuery.data?.state)
    .returnType<EntityHeaderFlag[]>()
    .with('PREPARATION', () => [
      {colorScheme: 'blue', label: i18n.t('entity.warehouse.labels.preparation')},
    ])
    .with('TRANSFER', () => [
      {colorScheme: 'yellow', label: i18n.t('entity.warehouse.labels.inTransfer')},
    ])
    .with('COMPLETED', () => [
      {colorScheme: 'green', label: i18n.t('entity.warehouse.labels.completed')},
    ])
    .otherwise(() => [{colorScheme: 'neutral', label: i18n.t('entity.warehouse.labels.draft')}]);

  const headerTitle = isNilOrEmpty(transferOrderId)
    ? i18n.t('page.warehouse.labels.newTransferOrder')
    : `${i18n.t('entity.warehouse.labels.transferOrder')} ${defaultTo('', transferOrderQuery.data?.number)}`;

  const headerParameters = isDraft
    ? [variantParameter]
    : [
        numberParameter,
        destinationTypeParameter,
        variantParameter,
        dateCreatedParameter,
        createdByParameter,
      ];

  const staticActions: EntityHeaderAction[] = isDraft
    ? [
        {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('general.actions.discard'),
          onClick: () => navigate(composePath(warehouseRoutes.transferOrderCreate)),
        },
      ]
    : [];

  const header: DetailTemplateHeader = {
    recordId: transferOrderQuery.data?.transferOrderId,
    resourceId: EntityResourceIds.warehouseTransferOrder,
    icon: 'action/return_item',
    flags: headerFlags,
    title: headerTitle,
    parameters: headerParameters,
    actions: [...headerActions, ...staticActions],
    controls: isDraft ? undefined : ['ASSIGNEE'],
  };

  const draftOverviewHref = composePath(warehouseRoutes.transferOrderDraft, {
    params: {transferOrderVariantId},
  });

  const overviewHref = composePath(warehouseRoutes.transferOrderDetailOverview, {
    params: {id: transferOrderId},
  });

  const navigation = buildArray<NavigationItem>([])
    .add({
      id: 'transfer-order-detail-overview',
      label: i18n.t('page.warehouse.labels.overview'),
      href: isDraft ? draftOverviewHref : overviewHref,
      content: <Overview />,
    })
    .when(isNotNilOrEmpty(transferOrderId), {
      id: 'transfer-order-detail-issue-notes',
      label: i18n.t('page.warehouse.labels.issueNotes'),
      href: composePath(warehouseRoutes.transferOrderDetailIssueNotes, {
        params: {id: transferOrderId},
      }),
      content: <IssueNotes />,
      badge: transferOrderQuery.data?.issueNoteCount
        ? {value: transferOrderQuery.data?.issueNoteCount, colorScheme: 'neutral'}
        : undefined,
    })
    .when(isNotNilOrEmpty(transferOrderId), {
      id: 'transfer-order-detail-document',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.transferOrderDetailDocuments, {
        params: {id: transferOrderId},
      }),
      content: <Documents onDocumentAction={refreshDocumentsCount} />,
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
    })
    .when(
      isNotNilOrEmpty(transferOrderId) && isFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST),
      () => ({
        id: 'transfer-order-detail-checklists',
        label: i18n.t('entity.checklist.labels.title'),
        href: composePath(warehouseRoutes.transferOrderDetailChecklists, {
          params: {id: transferOrderId},
        }),
        content: (
          <Section>
            <Checklists
              {...checklistSettings}
              contextTarget="warehouse-transfer-order"
              contextId={transferOrderId}
            />
          </Section>
        ),
        badge: badgeSettings,
        'data-testid': testIds.warehouse.transferOrderDetailChecklists('checklists'),
      })
    );

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.transferOrderDetail')} />
      <DetailTemplate
        key={generateHashFromObjects(transferOrderQuery.data)}
        isLoading={isLoading}
        isError={hasError}
        isLocked={headerActionStatus.isLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.transferOrderDetailOverview('page')}
      />
    </>
  );
}
